/**
 * Feature flags: the standard.
 *
 * A flag is a named boolean that switches behaviour at runtime without a
 * rebuild. Every flag is declared here, once, in `featureFlagDefinitions`;
 * nothing else in the app may invent a flag name.
 *
 * Naming: camelCase, describing the capability, not the ticket
 * (`communityFibre`, not `cfEnabled`). The same key is the URL parameter,
 * the localStorage entry and the redux field.
 *
 * Defaults: a new flag defaults to `false` so shipping it changes nothing.
 * Turning it on for everyone is a change of `defaultValue` here, followed
 * (once the old behaviour is dead) by deleting the flag and its branches.
 *
 * Resolution, lowest to highest precedence:
 *   1. `defaultValue` from the definition;
 *   2. the browser's saved overrides (the toggles on the /flags page,
 *      persisted in localStorage, so they survive reloads on that browser);
 *   3. the URL (`?communityFibre=true`), for the current session only,
 *      never saved: a marketing or support link must not change a browser
 *      for good.
 * `sources` records which of the three won for each flag, for the /flags page.
 *
 * `status: 'placeholder'` marks a flag that is declared ahead of the code
 * that will read it, so the toggle exists but does nothing yet.
 */

export type FeatureFlagKey = 'communityFibre' | 'communityFibreNpis' | 'remoteActivation' | 'hideEveningSpeedToggle' | 'promoPricing' | 'freeTextDisclaimers' | 'planCardPoints';

export type FeatureFlags = Record<FeatureFlagKey, boolean>;

export type FeatureFlagSource = 'default' | 'stored' | 'url';

export interface FeatureFlagDefinition {
  key: FeatureFlagKey;
  label: string;
  description: string;
  defaultValue: boolean;
  status: 'active' | 'placeholder';
  /** When the flag was introduced (YYYY-MM-DD), so stale flags stand out. */
  since: string;
  /**
   * A flag that only means something while another is on. It keeps its own
   * value either way, but is inert (see isFlagEffective) and shown nested
   * under its parent on the /flags page.
   */
  requires?: FeatureFlagKey;
}

export const featureFlagDefinitions: Record<FeatureFlagKey, FeatureFlagDefinition> = {
  communityFibre: {
    key: 'communityFibre',
    label: 'Community Fibre',
    description: 'Qualify addresses on the Community Fibre network too. Community Fibre is '
      + 'preferred over DGtek where both are available, and its plans (cf-100-100 to '
      + 'cf-2000-200) are shown. Off: Community Fibre addresses are not available.',
    defaultValue: false,
    status: 'active',
    since: '2026-09-25',
  },
  communityFibreNpis: {
    key: 'communityFibreNpis',
    label: 'Community Fibre: use the NPIS API',
    description: 'Qualify addresses and place orders through Community Fibre\'s NPIS '
      + 'interface (the NBN-style API) instead of the usual one (MEF). Only matters '
      + 'while Community Fibre is on. Leave off unless engineering asks.',
    defaultValue: false,
    status: 'active',
    since: '2026-10-06',
    requires: 'communityFibre',
  },
  remoteActivation: {
    key: 'remoteActivation',
    label: 'Remote activation',
    description: 'Offer remote activation (no technician visit) where the unit already has '
      + 'equipment. Placeholder: nothing reads this flag yet.',
    defaultValue: false,
    status: 'placeholder',
    since: '2026-09-25',
  },
  hideEveningSpeedToggle: {
    key: 'hideEveningSpeedToggle',
    label: 'Hide evening speed toggle',
    description: 'Hide the "Plan speed / Typical evening speed 7-11pm" switch on the plan cards.',
    defaultValue: false,
    status: 'active',
    since: '2026-08-01',
  },
  promoPricing: {
    key: 'promoPricing',
    label: 'Promo pricing',
    description: 'Plan cards price with the plan\'s own promo code (the promo price and '
      + '"with promo code X" until the customer applies one), and checkout and the order '
      + 'summary state the RRP billed after the promotional period. Off: the plan price as '
      + 'before, and "the standard retail rate" with no figure.',
    defaultValue: false,
    status: 'active',
    since: '2026-10-21',
  },
  freeTextDisclaimers: {
    key: 'freeTextDisclaimers',
    label: 'Free text disclaimers',
    description: 'Show the free text disclaimers from the marketing portal.',
    defaultValue: false,
    status: 'active',
    since: '2026-11-03',
  },
  planCardPoints: {
    key: 'planCardPoints',
    label: 'Plan card points',
    description: 'Plan cards list the points set for each plan in the marketing portal, then '
      + '"Typical evening speed* <down>/<up>Mbps" as the last point. Off: the three fixed '
      + 'points as before.',
    defaultValue: false,
    status: 'active',
    since: '2026-11-05',
  },
};

export const featureFlagKeys = Object.keys(featureFlagDefinitions) as FeatureFlagKey[];

/** Whether the flag's value counts: a dependent flag needs its parent on. */
export const isFlagEffective = (key: FeatureFlagKey, flags: FeatureFlags): boolean => {
  const parent = featureFlagDefinitions[key].requires;
  return parent === undefined || isFlagEffective(parent, flags) && flags[parent];
};

/** Community Fibre's two API surfaces. MEF is the default; NPIS is the NBN-style one. */
export type CommunityFibreSurface = 'mef' | 'npis';

/**
 * Which Community Fibre surface sequoia qualifies on and the backend orders
 * on. NPIS only when Community Fibre itself is on: the NPIS flag alone does
 * nothing, because nothing Community Fibre happens without the parent.
 */
export const communityFibreSurface = (flags: FeatureFlags): CommunityFibreSurface => (
  isFlagEffective('communityFibreNpis', flags) && flags.communityFibreNpis ? 'npis' : 'mef'
);

export const defaultFeatureFlags: FeatureFlags = featureFlagKeys.reduce(
  (acc, key) => ({ ...acc, [key]: featureFlagDefinitions[key].defaultValue }),
  {} as FeatureFlags,
);

/** localStorage entry holding the saved overrides, as a JSON object of key → boolean. */
export const FEATURE_FLAGS_STORAGE_KEY = 'pineapple.signup.featureFlags';

const isFlagKey = (key: string): key is FeatureFlagKey => key in featureFlagDefinitions;

const boolParam = (value: unknown): boolean | undefined => {
  if (typeof value !== 'string' || value === '') return undefined;
  const v = value.toLowerCase();
  if (v === 'true' || v === '1' || v === 'on') return true;
  if (v === 'false' || v === '0' || v === 'off') return false;
  return undefined;
};

/** Flags named in already-parsed URL query params; unknown or malformed values are ignored. */
export const featureFlagsFromUrl = (parsed: Record<string, unknown>): Partial<FeatureFlags> => (
  featureFlagKeys.reduce((acc, key) => {
    const value = boolParam(parsed[key]);
    return value === undefined ? acc : { ...acc, [key]: value };
  }, {} as Partial<FeatureFlags>)
);

/**
 * The browser's saved overrides. localStorage may be missing, blocked or
 * hold junk (private windows, cleared site data, a hand edit): every
 * failure reads as "no overrides".
 */
export const loadStoredFeatureFlags = (): Partial<FeatureFlags> => {
  try {
    const raw = window.localStorage.getItem(FEATURE_FLAGS_STORAGE_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return {};
    return Object.entries(parsed as Record<string, unknown>).reduce((acc, [key, value]) => (
      isFlagKey(key) && typeof value === 'boolean' ? { ...acc, [key]: value } : acc
    ), {} as Partial<FeatureFlags>);
  } catch {
    return {};
  }
};

export const saveStoredFeatureFlags = (overrides: Partial<FeatureFlags>): void => {
  try {
    if (Object.keys(overrides).length === 0) {
      window.localStorage.removeItem(FEATURE_FLAGS_STORAGE_KEY);
    } else {
      window.localStorage.setItem(FEATURE_FLAGS_STORAGE_KEY, JSON.stringify(overrides));
    }
  } catch {
    // Storage unavailable: the flags still apply for this page load.
  }
};

export interface ResolvedFeatureFlags {
  value: FeatureFlags;
  sources: Record<FeatureFlagKey, FeatureFlagSource>;
}

/** Applies the precedence: default < stored < url. */
export const resolveFeatureFlags = (
  stored: Partial<FeatureFlags>,
  fromUrl: Partial<FeatureFlags>,
): ResolvedFeatureFlags => featureFlagKeys.reduce((acc, key) => {
  let value = defaultFeatureFlags[key];
  let source: FeatureFlagSource = 'default';
  if (stored[key] !== undefined) {
    value = stored[key] as boolean;
    source = 'stored';
  }
  if (fromUrl[key] !== undefined) {
    value = fromUrl[key] as boolean;
    source = 'url';
  }
  return {
    value: { ...acc.value, [key]: value },
    sources: { ...acc.sources, [key]: source },
  };
}, { value: {} as FeatureFlags, sources: {} as Record<FeatureFlagKey, FeatureFlagSource> });
